import { PlusOutlined } from '@ant-design/icons';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { useCallback, useEffect, useState } from 'react';
import { DISABLED_LABEL } from '../lib/agent-state';
import { useAgentStore } from '../stores';
import { CreateAgentModal } from './AgentManager';
import { UserBadge } from './UserBadge';

interface Props {
  selectedAgentId: string | null;
  onSelectAgent: (agentId: string) => void;
}

export default function AgentSidebar({ selectedAgentId, onSelectAgent }: Props) {
  const { i18n } = useLingui();
  const agents = useAgentStore((s) => s.agents);
  const loadFailed = useAgentStore((s) => s.loadFailed);
  const loadAgents = useAgentStore((s) => s.loadAgents);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      await loadAgents();
    } finally {
      setLoading(false);
    }
  }, [loadAgents]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <span className="sidebar-title">
          <Trans>My Agents</Trans>
        </span>
        <div className="sidebar-actions">
          <button
            className="icon-btn"
            title={t(i18n)`Create Agent`}
            aria-label={t(i18n)`Create Agent`}
            onClick={() => setShowCreate(true)}
          >
            <PlusOutlined aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="channel-list">
        {loading ? (
          <div className="sidebar-empty">
            <Trans>Loading...</Trans>
          </div>
        ) : agents.length === 0 && loadFailed ? (
          // An owner with agents must never be told they have none.
          <div className="sidebar-empty" data-testid="agent-sidebar-load-failed">
            <Trans>Couldn’t load your agents.</Trans>{' '}
            <div className="sidebar-empty-actions">
              <button
                className="btn btn-sm"
                onClick={() => {
                  setLoading(true);
                  void load();
                }}
              >
                <Trans>Try again</Trans>
              </button>
            </div>
          </div>
        ) : agents.length === 0 ? (
          <div className="sidebar-empty">
            <Trans>No agents yet</Trans>
          </div>
        ) : (
          agents.map((agent) => (
            <button
              key={agent.id}
              className={`channel-item${selectedAgentId === agent.id ? ' channel-item-active' : ''}${agent.disabled ? ' channel-item-disabled' : ''}`}
              onClick={() => onSelectAgent(agent.id)}
            >
              <UserBadge
                userId={agent.id}
                displayName={agent.display_name}
                role="agent"
                avatarUrl={agent.avatar_url}
                state={agent.state}
                disabled={agent.disabled}
                size={28}
                style={{ flex: 1, minWidth: 0 }}
              />
              {agent.disabled && (
                <span className="agent-disabled-badge">{i18n._(DISABLED_LABEL)}</span>
              )}
            </button>
          ))
        )}
      </div>

      {showCreate && (
        <CreateAgentModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}
    </div>
  );
}
